import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useContext, useMemo, useRef, useState } from 'react';
import type { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { getCardBrandIcon } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext/ActivePosEntityContextV2';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { typography } from 'src/core/themes/shared.css';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useGetBuyerDetail } from 'src/hooks/api/useGetBuyerDetail';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import type { ModalProps } from 'src/modals/Modal';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ModalHeader } from 'src/modals/Modal/ModalHeader';
import { ExternalPaymentSection } from 'src/modals/RetailSale/components/ExternalPaymentSection';
import { PartialPaymentCoverageDialog } from 'src/modals/RetailSale/components/PartialPaymentCoverageDialog';
import { PartialPaymentInput } from 'src/modals/RetailSale/components/PartialPaymentInput';
import { getPaymentCountryCode } from 'src/modals/RetailSale/RetailSaleLocalContext.utils';
import { ContentId } from 'src/utils/constants/contentId';
import { ExternalPaymentMethodType } from 'src/WebApiController';
import {
  BuyerWithSalesDto,
  MarketplacePaymentState,
  SaleDetails,
  SaleLineItemCoverage,
} from 'src/WebApiController';

import * as styles from './MarketplacePaymentDialog.css';

type ModalConfig = Omit<ModalProps, 'fullscreen'>;

type PayState = 'configuring' | 'paying';

type PaymentMethodChoice = 'card' | 'external';

type AdyenSessionState = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type MarketplacePaymentDialogProps = {
  sale: SaleDetails;
  cancelTo: ModalConfig | null;
  initialPayState?: PayState;
  initialAdyenSession?: AdyenSessionState | null;
};

export const MarketplacePaymentDialog = ({
  sale,
  cancelTo,
  initialPayState = 'configuring',
  initialAdyenSession = null,
}: MarketplacePaymentDialogProps) => {
  const queryClient = useQueryClient();
  const { setModal, closeModal, currentModalConfig } = useContext(ModalContext);
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const { showErrorDialog } = useClientLogContext();

  // We should use the trans destination email as the buyer email
  // and avoid using the vendor account email (which is the POS seller account)
  // TODO: do we allow payments from a different buyer for the same retail sale?
  const { data: buyerDetail } = useGetBuyerDetail(
    sale.transDest?.email ?? null
  );
  const { data: knownCountries } = useKnownCountries();
  const countryCode = getPaymentCountryCode(
    buyerDetail as BuyerWithSalesDto | undefined,
    knownCountries
  );

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();

  const { data: savedPaymentMethods = [] } = useGetBuyerPaymentMethods(
    buyerDetail?.buyerEmail
  );

  const remainingAmount = useMemo(
    () =>
      (sale.mkpLines ?? [])
        .filter((l) => l.state === MarketplacePaymentState.Unpaid)
        .reduce((sum, l) => sum + l.amt.amt, 0),
    [sale.mkpLines]
  );

  const currencyCode = useMemo(
    () => sale.mkpLines?.[0]?.amt.currency ?? 'USD',
    [sale.mkpLines]
  );

  // ── Shared state ──────────────────────────────────────────────────────────
  const [paymentMethodChoice, setPaymentMethodChoice] =
    useState<PaymentMethodChoice>('card');
  const [payState, setPayState] = useState<PayState>(initialPayState);
  const [adyenSession, setAdyenSession] = useState<AdyenSessionState | null>(
    initialAdyenSession ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState(remainingAmount);
  const [selectedStoredPaymentMethodId, setSelectedStoredPaymentMethodId] =
    useState<string | null>(null);
  const merchantRef = useRef<string | null>(null);

  // ── External payment state (useState, no react-hook-form) ─────────────────
  const [externalPaymentMethodType, setExternalPaymentMethodType] = useState<
    ExternalPaymentMethodType | undefined
  >(undefined);
  const [externalPaymentNote, setExternalPaymentNote] = useState<string>('');
  const [externalPaymentDate, setExternalPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [externalMethodTypeError, setExternalMethodTypeError] = useState<
    string | undefined
  >(undefined);

  const handlePaymentChoiceChange = (value: string) => {
    setPaymentMethodChoice(value as PaymentMethodChoice);
    // Reset external validation error when switching modes
    setExternalMethodTypeError(undefined);
  };

  const handleClose = () => {
    setAdyenSession(null);
    merchantRef.current = null;
    if (cancelTo) {
      setModal(cancelTo);
    } else {
      closeModal(true);
    }
  };

  // ── Invalidate and refresh sale after a payment ───────────────────────────
  const invalidateAndRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['getRetailSaleDetails', sale.id],
    });
    await setActivePosEntity(sale.id, sale.idOnMkp, true);
  };

  // ── External partial: coverage confirmed ─────────────────────────────────
  const handleExternalCoverageConfirmed = async (
    coverages: SaleLineItemCoverage[]
  ) => {
    setIsLoading(true);
    try {
      await applyExternalPayment.mutateAsync({
        saleId: sale.id,
        input: {
          externalPaymentMethodType: externalPaymentMethodType!,
          externalPaymentNote: externalPaymentNote || undefined,
          externalPaymentAmount: partialAmount,
          externalPaymentDate: externalPaymentDate
            ? new Date(externalPaymentDate).toISOString()
            : undefined,
          coverages,
        },
      });
      await invalidateAndRefresh();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = async () => {
    if (!merchantRef.current) {
      merchantRef.current = `Retail-${sale.id}-${Date.now()}`;
    }

    // ── External payment flow ─────────────────────────────────────────────
    if (paymentMethodChoice === 'external') {
      // Validate that a method type has been selected
      if (externalPaymentMethodType == null) {
        setExternalMethodTypeError('Payment Method Type is required');
        return;
      }
      setExternalMethodTypeError(undefined);

      if (isPartialPayment) {
        // External + partial: open coverage dialog, then call ApplyExternalPayment
        const thisDialogConfig = currentModalConfig;
        setModal({
          children: (
            <PartialPaymentCoverageDialog
              salesGroupId={sale.salesGroupId}
              partialAmount={partialAmount}
              currencyCode={currencyCode}
              onConfirm={handleExternalCoverageConfirmed}
              onCancel={() => {
                if (thisDialogConfig) {
                  setModal(thisDialogConfig);
                } else {
                  closeModal(true);
                }
              }}
            />
          ),
        });
        return;
      }

      // External + full: call ApplyExternalPayment directly
      setIsLoading(true);
      try {
        await applyExternalPayment.mutateAsync({
          saleId: sale.id,
          input: {
            externalPaymentMethodType: externalPaymentMethodType,
            externalPaymentNote: externalPaymentNote || undefined,
            externalPaymentAmount: remainingAmount,
            externalPaymentDate: externalPaymentDate
              ? new Date(externalPaymentDate).toISOString()
              : undefined,
            coverages: undefined,
          },
        });
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── Adyen flow (card) — UNCHANGED ─────────────────────────────────────
    if (isPartialPayment) {
      // Capture values before navigating to coverage dialog (component unmounts)
      const thisDialogConfig = currentModalConfig;
      const capturedStoredMethodId = selectedStoredPaymentMethodId;
      const capturedMerchantRef = merchantRef.current;
      setModal({
        children: (
          <PartialPaymentCoverageDialog
            salesGroupId={sale.salesGroupId}
            partialAmount={partialAmount}
            currencyCode={currencyCode}
            onConfirm={async (coverages) => {
              setIsLoading(true);
              try {
                if (capturedStoredMethodId) {
                  await submitPaymentWithStoredMethod.mutateAsync({
                    salesGroupId: sale.salesGroupId,
                    storedPaymentMethodId: capturedStoredMethodId,
                    amount: partialAmount,
                    currencyCode,
                    buyerEmail: buyerDetail?.buyerEmail ?? null,
                    reference: capturedMerchantRef,
                    lineItemCoverages: coverages,
                  });
                  await queryClient.invalidateQueries({
                    queryKey: ['getRetailSaleDetails', sale.id],
                  });
                  await setActivePosEntity(sale.id, sale.idOnMkp, true);
                  handleClose();
                } else {
                  const session = await createPaymentSession.mutateAsync({
                    salesGroupId: sale.salesGroupId,
                    amount: partialAmount,
                    currencyCode,
                    buyerEmail: buyerDetail?.buyerEmail ?? null,
                    returnUrl: window.location.href,
                    reference: capturedMerchantRef,
                    countryCode,
                    lineItemCoverages: coverages,
                  });
                  setModal({
                    children: (
                      <MarketplacePaymentDialog
                        sale={sale}
                        cancelTo={cancelTo}
                        initialPayState="paying"
                        initialAdyenSession={{
                          ...session,
                          countryCode,
                          lineItemCoverages: coverages,
                        }}
                      />
                    ),
                  });
                }
              } finally {
                setIsLoading(false);
              }
            }}
            onCancel={() => {
              if (thisDialogConfig) {
                setModal(thisDialogConfig);
              } else {
                closeModal(true);
              }
            }}
          />
        ),
      });
      return;
    }
    // Full payment (Adyen)
    setIsLoading(true);
    try {
      if (selectedStoredPaymentMethodId) {
        await submitPaymentWithStoredMethod.mutateAsync({
          salesGroupId: sale.salesGroupId,
          storedPaymentMethodId: selectedStoredPaymentMethodId,
          amount: remainingAmount,
          currencyCode,
          buyerEmail: buyerDetail?.buyerEmail ?? null,
          reference: merchantRef.current,
          lineItemCoverages: null,
        });
        await queryClient.invalidateQueries({
          queryKey: ['getRetailSaleDetails', sale.id],
        });
        await setActivePosEntity(sale.id, sale.idOnMkp, true);
        merchantRef.current = null;
        handleClose();
      } else {
        const session = await createPaymentSession.mutateAsync({
          salesGroupId: sale.salesGroupId,
          amount: remainingAmount,
          currencyCode,
          buyerEmail: buyerDetail?.buyerEmail ?? null,
          returnUrl: window.location.href,
          reference: merchantRef.current,
          countryCode,
          lineItemCoverages: null,
        });
        setAdyenSession({
          ...session,
          countryCode,
          lineItemCoverages: null,
        });
        setPayState('paying');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPaymentCompleted = async (result: AdyenPaymentCompletedResult) => {
    if (!adyenSession) return;
    if (result.resultCode !== 'Authorised') {
      showErrorDialog('AdyenPayment', {
        message: `Payment not authorized: ${result.resultCode}`,
      });
      setAdyenSession(null);
      setPayState('configuring');
      return;
    }
    setIsLoading(true);
    try {
      await submitPaymentResult.mutateAsync({
        salesGroupId: sale.salesGroupId,
        input: {
          resultCode: result.resultCode,
          sessionResult: result.sessionResult ?? null,
          merchantReference: merchantRef.current ?? null,
          pspReference: null,
          lineItemCoverages: adyenSession.lineItemCoverages,
        },
      });
      // Invalidate retail sale details cache
      await queryClient.invalidateQueries({
        queryKey: ['getRetailSaleDetails', sale.id],
      });
      // Refresh the SaleDetails modal
      await setActivePosEntity(sale.id, sale.idOnMkp, true);
      merchantRef.current = null;
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const isProceedDisabled = useMemo(() => {
    if (isLoading) return true;
    if (
      isPartialPayment &&
      (partialAmount <= 0 || partialAmount >= remainingAmount)
    )
      return true;
    return false;
  }, [isLoading, isPartialPayment, partialAmount, remainingAmount]);

  const isExternalPayment = paymentMethodChoice === 'external';

  return (
    <>
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            <Content id={ContentId.PayNow} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <ModalBodyDataContainer style={{ height: '100%' }}>
          {payState === 'configuring' && (
            <>
              {/* Top-level payment method radio — "Pay with Card" vs "Pay Externally" */}
              <RadioGroup
                value={paymentMethodChoice}
                onValueChange={handlePaymentChoiceChange}
                disabled={isLoading}
                className={styles.radioGroup}
              >
                <Radio
                  value="card"
                  label="Pay with Card"
                  disabled={isLoading}
                />
                <Radio
                  value="external"
                  label="Pay Externally (Cash / Wire / Other)"
                  disabled={isLoading}
                />
              </RadioGroup>

              {/* Partial payment toggle — visible for BOTH card and external flows */}
              <PartialPaymentInput
                disabled={isLoading}
                totalAmount={remainingAmount}
                currencyCode={currencyCode}
                isPartialPayment={isPartialPayment}
                partialAmount={partialAmount}
                onIsPartialPaymentChange={setIsPartialPayment}
                onPartialAmountChange={setPartialAmount}
              />

              {/* Card (Adyen) flow — saved cards + add new card */}
              {!isExternalPayment && savedPaymentMethods.length > 0 && (
                <div className={styles.savedCardsSection}>
                  {savedPaymentMethods.map((card) => {
                    const cardId = card.storedPaymentMethodId;
                    const isSelected = selectedStoredPaymentMethodId === cardId;
                    return (
                      <div
                        key={cardId}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        className={clsx(styles.savedCardRow, {
                          [styles.savedCardRowSelected]: isSelected,
                        })}
                        onClick={
                          isLoading
                            ? undefined
                            : () => setSelectedStoredPaymentMethodId(cardId)
                        }
                        onKeyDown={
                          isLoading
                            ? undefined
                            : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setSelectedStoredPaymentMethodId(cardId);
                                }
                              }
                        }
                      >
                        <input
                          type="radio"
                          readOnly
                          checked={isSelected}
                          aria-hidden="true"
                        />
                        <div className={styles.savedCardInfo}>
                          <span className={styles.savedCardNumber}>
                            {getCardBrandIcon(card.cardBrand)} ****{' '}
                            {card.cardSummary}
                          </span>
                          {card.expiryMonth != null &&
                            card.expiryYear != null && (
                              <span className={styles.savedCardExpiry}>
                                <Content id={ContentId.Expires} />{' '}
                                {card.expiryMonth}/{card.expiryYear}
                              </span>
                            )}
                        </div>
                        {card.isDefault && (
                          <span className={styles.savedCardBadge}>
                            <Content id={ContentId.Default} />
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => setSelectedStoredPaymentMethodId(null)}
                    className={clsx(styles.addNewCardRow, {
                      [styles.addNewCardRowSelected]:
                        selectedStoredPaymentMethodId === null,
                    })}
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={selectedStoredPaymentMethodId === null}
                      aria-hidden="true"
                    />
                    <Content id={ContentId.AddNewCard} />
                  </button>
                </div>
              )}

              {/* External payment form — controlled mode (no react-hook-form) */}
              {isExternalPayment && (
                <div style={{ marginTop: '12px' }}>
                  <ExternalPaymentSection
                    disabled={isLoading}
                    totalAmount={
                      isPartialPayment ? partialAmount : remainingAmount
                    }
                    controlled={{
                      methodType: externalPaymentMethodType,
                      note: externalPaymentNote,
                      date: externalPaymentDate,
                      currencyCode,
                      methodTypeError: externalMethodTypeError,
                      onMethodTypeChange: setExternalPaymentMethodType,
                      onNoteChange: setExternalPaymentNote,
                      onDateChange: setExternalPaymentDate,
                    }}
                  />
                </div>
              )}
            </>
          )}

          {payState === 'paying' && adyenSession && (
            <div className={styles.adyenContainer}>
              <AdyenDropIn
                sessionId={adyenSession.sessionId}
                sessionData={adyenSession.sessionData}
                clientKey={adyenSession.clientKey}
                environment={adyenSession.environment}
                countryCode={adyenSession.countryCode}
                onPaymentCompleted={onPaymentCompleted}
                onError={(error) => {
                  showErrorDialog('AdyenPayment', { message: error.message });
                  setAdyenSession(null);
                  setPayState('configuring');
                }}
              />
            </div>
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack justifyContent="end" gap="m">
          <CancelButton onClick={handleClose} disabled={isLoading} />
          {payState === 'configuring' && (
            <OkButton
              textContentId={ContentId.Confirm}
              variant="regular"
              onClick={handleProceed}
              disabled={isProceedDisabled}
            />
          )}
        </Stack>
      </ModalFooter>
    </>
  );
};
